import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { dbHelpers } from '../../lib/supabase'
import SafeIcon from '../../common/SafeIcon'
import StatsCard from './StatsCard'
import RecentTrades from './RecentTrades'
import PerformanceChart from './PerformanceChart'
import * as FiIcons from 'react-icons/fi'

const { FiTrendingUp, FiDollarSign, FiTarget, FiActivity, FiPlus } = FiIcons

const Dashboard = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [trades, setTrades] = useState([])
  const [stats, setStats] = useState({ totalTrades: 0, winRate: 0, totalPnL: 0, profitFactor: 0 })
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (user) {
      loadDashboardData()
    } else {
      setLoading(false)
    }
  }, [user])

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      const tradesData = await dbHelpers.getUserTrades(user.id, 10)
      setTrades(tradesData || [])
      
      const totalTrades = tradesData.length
      const winningTrades = tradesData.filter(t => (t.pnl || 0) > 0)
      const losingTrades = tradesData.filter(t => (t.pnl || 0) < 0)
      
      const winRate = totalTrades > 0 ? (winningTrades.length / totalTrades) * 100 : 0
      const totalPnL = tradesData.reduce((sum, t) => sum + (t.pnl || 0), 0)
      
      const totalWins = winningTrades.reduce((sum, t) => sum + (t.pnl || 0), 0)
      const totalLosses = Math.abs(losingTrades.reduce((sum, t) => sum + (t.pnl || 0), 0))
      
      const profitFactor = totalLosses > 0 ? totalWins / totalLosses : (totalWins > 0 ? 99.9 : 0)
      
      setStats({ totalTrades, winRate, totalPnL, profitFactor })
    } catch (error) {
      console.error('Dashboard data error:', error)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-64 space-y-4">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
        <p className="text-gray-500 animate-pulse">Loading dashboard data...</p>
      </div>
    )
  }

  const statsCards = [
    { title: 'Total Trades', value: stats.totalTrades, icon: FiActivity, color: 'blue', format: 'number' },
    { title: 'Win Rate', value: stats.winRate, icon: FiTarget, color: 'green', format: 'percentage' },
    { title: 'Total P&L', value: stats.totalPnL, icon: FiDollarSign, color: stats.totalPnL >= 0 ? 'green' : 'red', format: 'currency' },
    { title: 'Profit Factor', value: stats.profitFactor, icon: FiTrendingUp, color: stats.profitFactor >= 1 ? 'green' : 'red', format: 'decimal' }
  ]

  return (
    <div className="space-y-8">
      <motion.div 
        initial={{ opacity: 0, y: 20 }} 
        animate={{ opacity: 1, y: 0 }} 
        className="flex justify-between items-center"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Trading Dashboard</h1>
          <p className="text-gray-600 mt-1">Track your trading performance and insights</p>
        </div>
        <button 
          onClick={() => navigate('/journal', { state: { openForm: true } })}
          className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-all shadow-sm hover:shadow-md"
        >
          <SafeIcon icon={FiPlus} className="h-4 w-4" />
          <span className="hidden sm:inline">Add Trade</span>
        </button>
      </motion.div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statsCards.map((card, index) => (
          <motion.div key={card.title} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }}>
            <StatsCard {...card} />
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
          <PerformanceChart trades={trades} />
        </motion.div>
        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.3 }}>
          <RecentTrades trades={trades} />
        </motion.div>
      </div>
    </div>
  )
}

export default Dashboard